import { useEffect, useState, useRef, useCallback } from 'react';

interface AutoDimConfig {
  dimStartHour?: number;  // Hour to start dimming (24h format, default 21 = 9PM)
  dimEndHour?: number;    // Hour to end dimming (24h format, default 7 = 7AM)
  dimOpacity?: number;    // Dimmed opacity (0-1, default 0.3)
  fullBrightness?: number; // Full brightness (0-1, default 1)
  idleTimeout?: number;    // Minutes before going dark when idle (default 5)
}

export const useAutoDim = (config: AutoDimConfig = {}) => {
  const {
    dimStartHour = 21,    // 9 PM
    dimEndHour = 7,       // 7 AM
    dimOpacity = 0.3,     // 30% brightness when dimmed
    fullBrightness = 1,   // 100% brightness when active
    idleTimeout = 5       // 5 minutes
  } = config;

  const [brightness, setBrightness] = useState(fullBrightness);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [isIdle, setIsIdle] = useState(false);

  // Use ref to track brightness so event handlers always have current value
  // without needing to recreate listeners (fixes memory leak)
  const brightnessRef = useRef(brightness);

  // Keep ref in sync with state
  useEffect(() => {
    brightnessRef.current = brightness;
  }, [brightness]);

  // Check if current time is in dim hours
  const isDimTime = useCallback(() => {
    const now = new Date();
    const hour = now.getHours();

    if (dimStartHour > dimEndHour) {
      // Crosses midnight (e.g., 9PM to 7AM)
      return hour >= dimStartHour || hour < dimEndHour;
    } else {
      // Same day (e.g., 1AM to 7AM)
      return hour >= dimStartHour && hour < dimEndHour;
    }
  }, [dimStartHour, dimEndHour]);

  // Handle user activity - uses ref to get current brightness
  const handleActivity = useCallback(() => {
    setLastActivity(Date.now());
    setIsIdle(false);

    // Wake up screen if it was dark (use ref for current value)
    if (brightnessRef.current === 0) {
      setBrightness(isDimTime() ? dimOpacity : fullBrightness);
    }
  }, [isDimTime, dimOpacity, fullBrightness]);

  // Set up activity listeners - only created once on mount
  useEffect(() => {
    const events = ['mousedown', 'touchstart', 'keydown', 'scroll'];

    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [handleActivity]);

  // Check time and idle status
  useEffect(() => {
    const checkBrightness = () => {
      const now = Date.now();
      const idleMinutes = (now - lastActivity) / 1000 / 60;

      // Check if idle
      if (idleMinutes >= idleTimeout) {
        if (!isIdle) {
          setIsIdle(true);
          // Go completely dark when idle during dim hours
          setBrightness(isDimTime() ? 0 : dimOpacity);
        }
      } else {
        // Not idle - set brightness based on time
        const targetBrightness = isDimTime() ? dimOpacity : fullBrightness;
        // Use ref to avoid re-running effect on every brightness change
        if (brightnessRef.current !== targetBrightness && !isIdle) {
          setBrightness(targetBrightness);
        }
      }
    };

    // Check immediately
    checkBrightness();

    // Check every minute
    const interval = setInterval(checkBrightness, 60000);

    return () => clearInterval(interval);
  }, [lastActivity, isIdle, dimOpacity, fullBrightness, idleTimeout, isDimTime]);

  return {
    brightness,
    isIdle,
    isDimmed: brightness < fullBrightness,
    wakeUp: handleActivity
  };
};